import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import BreakpointsContainer from '~/components/BreakpointsContainer';
import { Image } from '~/components/Image';
import MarkDownRender from '~/components/Markdown/MarkDownRender';
import PostAuthors from '~/components/PostCardv3/PostAuthors';
import Signup from '~/components/Signup';
import SocialIcons from '~/components/SocialIcons';
import Typography from '~/components/Typography';

import type { MainSectionBackgroundSize } from '~/interfaces/home_page';
import type { Publication } from '~/interfaces/publication';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useAuthors from '~/hooks/useLoaders/useAuthors';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import { getAuthors } from '~/services/exampleSite/authors';

import { BG_OPACITY_OPTIONS, BORDER_RADIUS_OPTIONS, PADDING_OPTIONS, SIDE_OPTIONS } from '~/utils/designOptions';

import type { HomePageLoaderData } from '~/routes';

const generateBorderSidesClass = (width: any, sides: any) => {
  const options = SIDE_OPTIONS[sides];

  return options[width];
};

const generateBackgroundSizeStyle = (
  bgSize: MainSectionBackgroundSize,
  bgWidth: number | null,
  bgHeight: number | null,
) => {
  switch (bgSize) {
    case 'auto':
      return {
        backgroundSize: 'cover',
      };
    case 'custom':
      return {
        backgroundSize: `${bgWidth}px ${bgHeight}px`,
      };
    case 'auto-height':
      return {
        backgroundSize: `${bgWidth}px auto`,
      };
    case 'auto-width':
      return {
        backgroundSize: `auto ${bgHeight}px`,
      };
  }
};

const checkForSocialIcons = (publication: Publication) => {
  const tiktokUrl = publication.tiktok_url;
  const twitterUrl = publication.twitter_url;
  const youtubeUrl = publication.youtube_url;
  const facebookUrl = publication.facebook_url;
  const instagramUrl = publication.instagram_url;
  const linkedInUrl = publication.linkedin_url;
  const discordUrl = publication.discord_url;
  const threadsUrl = publication.threads_url;
  const blueskyUrl = publication.bluesky_url;
  const isAtLeastOnePresent =
    tiktokUrl ||
    twitterUrl ||
    youtubeUrl ||
    facebookUrl ||
    instagramUrl ||
    linkedInUrl ||
    discordUrl ||
    threadsUrl ||
    blueskyUrl;

  return isAtLeastOnePresent;
};

const Default = () => {
  const { t } = useTranslation();
  const { isLoading, authors } = useAuthors();
  const { homePage, publication, signupWidgetConfigs, showExampleSite } = useTypedLoaderData<HomePageLoaderData>();
  const renderableAuthors = getAuthors({
    isExampleSite: showExampleSite,
    authors,
  });
  const { general, content_container, headline, description, logo } = homePage.main_section;

  const opacityClass = BG_OPACITY_OPTIONS[general.bg_gloss_opacity || '0'];

  const isBGTransparent = content_container.bg_color === 'transparent';
  const isBorderTransparent = content_container.border_color === 'transparent';
  const noPadding = isBGTransparent && isBorderTransparent;

  const borderRadiusClass = BORDER_RADIUS_OPTIONS[content_container.border_radius || 'rounded-none'];

  const borderClass = generateBorderSidesClass(content_container.border_width, content_container.border_sides);

  const paddingClass = noPadding ? 'p-0' : PADDING_OPTIONS[content_container.padding || 'p-0'];
  const hasSocialIcons = checkForSocialIcons(publication);
  const { web_template } = publication;
  const isLoggedIn = useIsLoggedIn();
  const authorsLabelText = homePage.main_section.authors_widget.label || t('author.byline');
  const authorsText = homePage.main_section.authors_widget.text || '';
  const bgImageUrl = homePage?.main_bg_image_url;

  return (
    <div
      className={'relative py-8'}
      style={{
        backgroundColor: general.bg_color,
        ...(bgImageUrl && { backgroundImage: `url(${bgImageUrl})` }),
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        ...generateBackgroundSizeStyle(
          homePage?.main_section.general.bg_size,
          homePage?.main_section.general.bg_width,
          homePage?.main_section.general.bg_height,
        ),
      }}
    >
      {bgImageUrl && (
        <div
          className={cx('absolute left-0 top-0 h-full w-full', opacityClass)}
          style={{
            backgroundColor: general.bg_color,
          }}
        />
      )}
      <div className="relative left-0 top-0 h-full w-full">
        <BreakpointsContainer>
          <div
            className={cx(
              'mx-auto mb-10 w-full',
              content_container.padding === 'none' ? 'max-w-2xl' : 'max-w-2xl',
              paddingClass,
              borderRadiusClass,
              borderClass,
            )}
            style={{
              borderColor: content_container.border_color,
              backgroundColor: content_container.bg_color,
            }}
          >
            <div className="flex-start flex flex-col items-center py-2">
              {logo.enabled && (
                <div
                  className={cx({
                    'mb-4 overflow-hidden rounded': true,
                    'h-16 w-16': logo.size === 'sm',
                    'h-20 w-20': logo.size === 'md',
                    'h-24 w-24': logo.size === 'lg',
                    'shadow-sm': logo.shadow === 'sm',
                    'shadow-md': logo.shadow === 'md',
                    'shadow-lg': logo.shadow === 'lg',
                    'shadow-xl': logo.shadow === 'xl',
                  })}
                >
                  <Image maxWidth={128} aspectRatio={'square'} src={publication.logo.url} alt={publication.name} />
                </div>
              )}
              <div className="space-y-4">
                <div className={'mx-auto w-full max-w-xl'}>
                  <Typography
                    as="h1"
                    size={headline.size}
                    weight="bold"
                    fontFamilyv2={headline.font}
                    color={headline.color}
                    className="w-full text-center"
                  >
                    <MarkDownRender>{headline.text}</MarkDownRender>
                  </Typography>
                </div>
                <Typography
                  as="div"
                  weight="regular"
                  size={description.size}
                  fontFamilyv2={description.font}
                  color={description.color}
                  className="w-full text-center"
                >
                  <MarkDownRender>{description.text}</MarkDownRender>
                </Typography>
              </div>
            </div>

            <div className="mx-auto mt-4 max-w-sm sm:max-w-lg">
              {isLoggedIn && web_template.logged_subscribe_form ? <></> : <Signup signupStyles={signupWidgetConfigs} />}
            </div>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4">
            {!isLoading && homePage?.main_section?.authors_widget?.enabled && (
              <div className="space-y-2">
                <Typography
                  as="span"
                  fontFamily={homePage.general_section.theme.font}
                  fontFamilyv2={homePage.general_section.theme.font}
                  color={homePage.main_section.authors_widget.text_color}
                  className="whitespace-nowrap text-xs font-semibold"
                >
                  {authorsLabelText}
                </Typography>
                <PostAuthors
                  authors={renderableAuthors}
                  color={homePage.main_section.authors_widget.text_color}
                  bgColor={'#FFFFFF'}
                  font={homePage.general_section.theme.font}
                  fallbackName={authorsText || publication.email_sender_name || publication.name}
                />
              </div>
            )}

            {homePage.main_section.social_widget.enabled && hasSocialIcons && (
              <div className="space-y-2">
                <Typography
                  as="p"
                  fontFamily={homePage.general_section.theme.font}
                  fontFamilyv2={homePage.general_section.theme.font}
                  color={homePage.main_section.social_widget.text_color}
                  className="whitespace-nowrap text-xs font-semibold"
                >
                  {homePage.main_section.social_widget.label || t('shared.cta.connect')}
                </Typography>
                <SocialIcons
                  publication={publication}
                  bgColor={homePage.main_section.social_widget.icons_bg_color}
                  fillColor={homePage.main_section.social_widget.icons_text_color}
                />
              </div>
            )}
          </div>
        </BreakpointsContainer>
      </div>
    </div>
  );
};

export default Default;
